import { parseReasonsJson, reasonsToDisplayStrings } from '../../utils/spikeReasons';
import type {
  ApiError,
  DetailedAnalytics,
  HourlyStatsResponse,
  SpikesByTickerResponse,
  SpikeApiItem,
  SpikeSignal,
  SpikeTopItem,
  SpikesTopResponse,
  PriceLinkageResponse,
  PriceLinkageSessionType,
  TopPriceLinkageResponse,
  TickerContribution,
  TopTickersResponse,
} from './types';
import type { ApiDomainDeps } from './domainDeps';

const isForbidden = (e: unknown): boolean => {
  const err = e as ApiError;
  return err?.status === 403 || err?.is403 === true;
};

function mapSpikeApiItemToSignal(item: SpikeApiItem): SpikeSignal {
  const parsed = parseReasonsJson(item.reasonsJson);
  const reasons = reasonsToDisplayStrings(parsed);
  return {
    level: item.level,
    score: item.score,
    reasons,
    window_metrics: { mentions: item.mentions, unique_users: item.totalUniqueUsers },
    window_start: item.windowStartUtc,
  };
}

export function createStatsDomain(deps: ApiDomainDeps) {
  return {
    async getTopTickersFree(
      hours: number = 6,
      limit: number = 10,
      sort: 'mentions' | 'sentiment' = 'mentions',
      order: 'asc' | 'desc' = 'desc'
    ): Promise<TopTickersResponse> {
      const freeHours = Math.min(hours, 12);
      const freeLimit = Math.min(limit, 10);
      const params = new URLSearchParams({
        hours: freeHours.toString(),
        limit: freeLimit.toString(),
        sort,
        order,
      });
      return deps.request<TopTickersResponse>(`/api/stats/free/top-tickers?${params}`);
    },

    async getTopTickersPro(
      hours: number = 24,
      limit: number = 20,
      sort: 'mentions' | 'sentiment' = 'mentions',
      order: 'asc' | 'desc' = 'desc'
    ): Promise<TopTickersResponse> {
      const proHours = Math.min(hours, 720);
      const proLimit = Math.min(limit, 100);
      const params = new URLSearchParams({
        hours: proHours.toString(),
        limit: proLimit.toString(),
        sort,
        order,
      });
      return deps.request<TopTickersResponse>(`/api/stats/pro/top-tickers?${params}`);
    },

    async getTopTickers(
      hours: number = 6,
      limit: number = 10,
      sort: 'mentions' | 'sentiment' = 'mentions',
      order: 'asc' | 'desc' = 'desc',
      isPro: boolean
    ): Promise<TopTickersResponse> {
      if (isPro) {
        try {
          return await this.getTopTickersPro(hours, limit, sort, order);
        } catch (e: unknown) {
          if (isForbidden(e)) {
            deps.downgradeSessionToFree();
            return this.getTopTickersFree(hours, limit, sort, order);
          }
          throw e;
        }
      }
      return this.getTopTickersFree(hours, limit, sort, order);
    },

    async getAnalytics(ticker: string, hours: number, interval: number, isPro: boolean): Promise<DetailedAnalytics> {
      if (isPro) {
        try {
          return await deps.request<DetailedAnalytics>(`/api/stats/pro/analytics?ticker=${ticker.toUpperCase()}&hours=${hours}&interval=${interval}`);
        } catch (e: unknown) {
          if (isForbidden(e)) {
            deps.downgradeSessionToFree();
            const freeHours = Math.min(hours, 12);
            return deps.request<DetailedAnalytics>(`/api/stats/free/analytics?ticker=${ticker.toUpperCase()}&hours=${freeHours}&interval=60`);
          }
          throw e;
        }
      }
      const freeHours = Math.min(hours, 12);
      return deps.request<DetailedAnalytics>(`/api/stats/free/analytics?ticker=${ticker.toUpperCase()}&hours=${freeHours}&interval=60`);
    },

    async getTimeSeries(
      hours: number = 24,
      ticker?: string,
      subreddit?: string,
      interval: string = '15min',
      isPro: boolean = false
    ): Promise<HourlyStatsResponse> {
      if (!isPro) {
        const freeHours = Math.min(hours, 12);
        const params = new URLSearchParams({ hours: freeHours.toString(), interval: '60' });
        if (ticker) params.append('ticker', ticker.toUpperCase());
        return deps.request<HourlyStatsResponse>(`/api/stats/free/time-series?${params}`);
      }
      try {
        const params = new URLSearchParams({ hours: hours.toString(), interval });
        if (ticker) params.append('ticker', ticker.toUpperCase());
        if (subreddit) params.append('subreddit', subreddit);
        return await deps.request<HourlyStatsResponse>(`/api/stats/pro/time-series?${params}`);
      } catch (e: unknown) {
        if (isForbidden(e)) {
          deps.downgradeSessionToFree();
          const freeHours = Math.min(hours, 12);
          const freeParams = new URLSearchParams({ hours: freeHours.toString(), interval: '60' });
          if (ticker) freeParams.append('ticker', ticker.toUpperCase());
          return deps.request<HourlyStatsResponse>(`/api/stats/free/time-series?${freeParams}`);
        }
        throw e;
      }
    },

    async getTickerExplain(ticker: string, limit: number = 10): Promise<TickerContribution[]> {
      const params = new URLSearchParams({ limit: String(limit) });
      return deps.request<TickerContribution[]>(`/api/aggregation/${ticker.toUpperCase()}/explain?${params}`);
    },

    async getSpikes(ticker: string, hours: number = 12): Promise<SpikesByTickerResponse> {
      const params = new URLSearchParams({
        ticker: ticker.toUpperCase(),
        hours: String(Math.min(Math.max(1, hours), 168)),
      });
      const raw = await deps.request<SpikeApiItem[] | SpikesByTickerResponse>(`/api/spikes?${params}`);
      const tickerUpper = ticker.toUpperCase();
      if (Array.isArray(raw)) {
        const spikes = raw.map(mapSpikeApiItemToSignal);
        return { ticker: tickerUpper, spikes };
      }
      if (raw.spikes && Array.isArray(raw.spikes)) {
        return { ticker: raw.ticker ?? tickerUpper, spikes: raw.spikes, hours: raw.hours };
      }
      return { ticker: tickerUpper, spikes: [] };
    },

    async getSpikesTop(limit: number = 20): Promise<SpikesTopResponse> {
      const limitClamped = Math.min(Math.max(1, limit), 100);
      const raw = await deps.request<SpikesTopResponse | SpikeApiItem[]>(`/api/spikes/top?limit=${limitClamped}`);
      if (Array.isArray(raw)) {
        const spikes = raw.map((item) => {
          const parsed = parseReasonsJson(item.reasonsJson);
          const reasons = reasonsToDisplayStrings(parsed);
          return {
            ticker: item.ticker,
            level: item.level,
            score: item.score,
            reasons,
            window_metrics: { mentions: item.mentions, unique_users: item.totalUniqueUsers },
            window_start: item.windowStartUtc,
          } as SpikeTopItem;
        });
        return { spikes };
      }
      if (raw.spikes && Array.isArray(raw.spikes)) {
        const spikes = raw.spikes.map((item: SpikeTopItem | SpikeApiItem) => {
          if ('reasonsJson' in item && item.reasonsJson) {
            const parsed = parseReasonsJson(item.reasonsJson);
            return { ...item, reasons: reasonsToDisplayStrings(parsed) } as SpikeTopItem;
          }
          return item as SpikeTopItem;
        });
        return { ...raw, spikes };
      }
      return raw;
    },

    async getSpikesTopTickers(hours: number = 3, limit: number = 20): Promise<SpikesTopResponse> {
      const hoursClamped = Math.min(Math.max(1, hours), 24);
      const limitClamped = Math.min(Math.max(1, limit), 50);
      const raw = await deps.request<SpikeApiItem[]>(`/api/spikes/top-tickers?hours=${hoursClamped}&limit=${limitClamped}`);
      if (!Array.isArray(raw)) return { spikes: [] };
      const spikes = raw.map((item) => {
        const parsed = parseReasonsJson(item.reasonsJson);
        const reasons = reasonsToDisplayStrings(parsed);
        return {
          ticker: item.ticker,
          level: item.level,
          score: item.score,
          reasons,
          window_metrics: { mentions: item.mentions, unique_users: item.totalUniqueUsers },
          window_start: item.windowStartUtc,
        } as SpikeTopItem;
      });
      return { spikes };
    },

    async getPriceLinkage(
      ticker: string,
      sessionType: PriceLinkageSessionType | string = 'REGULAR',
      windowDays: number = 60
    ): Promise<PriceLinkageResponse> {
      try {
        const params = new URLSearchParams({
          ticker: ticker.toUpperCase(),
          sessionType: String(sessionType),
          windowDays: String(Math.min(Math.max(7, windowDays), 365)),
        });
        return await deps.request<PriceLinkageResponse>(`/api/stats/pro/price-linkage?${params}`);
      } catch (e: unknown) {
        if (isForbidden(e)) {
          deps.downgradeSessionToFree();
        }
        throw e;
      }
    },

    async getTopPriceLinkage(
      limit: number = 20,
      sessionType: PriceLinkageSessionType | string = 'REGULAR',
      windowDays: number = 60
    ): Promise<TopPriceLinkageResponse> {
      try {
        const params = new URLSearchParams({
          limit: String(Math.min(Math.max(1, limit), 100)),
          sessionType: String(sessionType),
          windowDays: String(Math.min(Math.max(7, windowDays), 365)),
        });
        const raw = await deps.request<TopPriceLinkageResponse | PriceLinkageResponse[]>(`/api/stats/pro/price-linkage/top?${params}`);
        if (Array.isArray(raw)) return { items: raw, limit };
        return { ...raw, items: Array.isArray(raw.items) ? raw.items : [] };
      } catch (e: unknown) {
        if (isForbidden(e)) {
          deps.downgradeSessionToFree();
        }
        throw e;
      }
    },
  };
}
